import { AlertCircle, Check, FileText, Minus, Plus, Upload, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Slider } from "@/components/ui/slider"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { SampleTile } from "@/components/tuw/post/SampleTile"
import { countWords, resolveOptions, SERVER_WORD_MAX } from "@/lib/post/fieldValidation"
export { countWords } from "@/lib/post/fieldValidation"

// FieldRenderer — the switchboard over the 16 field types in CONFIG-SCHEMA §3.
//
//   field    — the field object ({ type, key, label, help, required, when, ... })
//   values   — the current service's posting sub-state (state.postings[svc])
//   setField — setField(key, value) updates one attr in that sub-state
//   error    — validation message string for this field (or null)
//   ctx      — read-only cross-cutting facts; ctx.options is the options.js module
//
// Composites (consent / triggerWarning) call setField on MULTIPLE carrier keys.
// Expansion of those carriers into backend booleans happens in serialize.js,
// NOT here.

// ── helpers ───────────────────────────────────────────────────────────────

function resolve(maybeFn, values, ctx, fallback) {
  if (typeof maybeFn === "function") return maybeFn(values, ctx)
  return maybeFn === undefined ? fallback : maybeFn
}

const CARD_BASE =
  "flex w-full items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors"
const CARD_ON = "border-primary bg-[var(--red-soft)]"
const CARD_OFF = "border-line-2 hover:border-ink-3"

// 3-color swatch bars for the palette field (design service.html `paletteField`
// `swatches`, keyed by the build's COLOR_PALETTES label). UI affordance only —
// the field still submits the backend palette KEY (Plumbline Phase 2 WI-A5).
const PALETTE_SWATCHES = {
  Aquas: ["#3AAFA9", "#7FD8D2", "#BFEAE6"],
  Blues: ["#2A6FDB", "#6FA0EE", "#BCD3F7"],
  "Dark Neutrals": ["#3A3A36", "#5B5B55", "#8A8A82"],
  Greens: ["#1F8A5B", "#5FB785", "#B6E0C6"],
  "Light Neutrals": ["#EDE8DF", "#DDD6C8", "#C9BEA9"],
  Oranges: ["#E07B39", "#F0A668", "#F8D2AE"],
  Pinks: ["#D85C8A", "#EE92B4", "#F8CBDC"],
  Purples: ["#7A5AD8", "#A78BE8", "#D2C4F2"],
  Reds: ["#D23B49", "#E8767F", "#F4BDC1"],
  Yellows: ["#E3B43A", "#F0CF73", "#F8E7AE"],
  "Designer Suggested": ["#1A1A17", "#9A9A91", "#E8E8E3"],
}

// ── presentational atoms ────────────────────────────────────────────────────

function FieldLabel({ children, optional, required, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="flex items-center gap-2 text-[12.5px] font-bold text-foreground"
    >
      {children}
      {optional && (
        <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
          Optional
        </span>
      )}
      {required && (
        <span className="rounded-full bg-[var(--red-soft)] px-2 py-0.5 text-[11px] font-semibold text-primary">
          Required
        </span>
      )}
    </label>
  )
}

function Help({ children }) {
  if (!children) return null
  return <p className="-mt-0.5 text-[12px] leading-snug text-muted-foreground">{children}</p>
}

function ErrorText({ children }) {
  if (!children) return null
  return (
    <p className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
      <AlertCircle className="size-3.5" strokeWidth={2.2} />
      {children}
    </p>
  )
}

function Wrap({ children }) {
  return <div className="flex flex-col gap-2">{children}</div>
}

// ── main ────────────────────────────────────────────────────────────────────

export function FieldRenderer({ field, values = {}, setField, error, ctx = {} }) {
  // render condition
  if (field.when && !field.when(values, ctx)) return null

  const required = !!resolve(field.required, values, ctx, false)
  const optional = !required
  const id = `f-${field.key}`
  const value = values[field.key]

  switch (field.type) {
    // ── text ────────────────────────────────────────────────────────────────
    case "text":
      return (
        <Wrap>
          <FieldLabel htmlFor={id} optional={optional}>
            {field.label}
          </FieldLabel>
          <Help>{field.help}</Help>
          <Input
            id={id}
            value={value ?? ""}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            aria-invalid={!!error}
            onChange={(e) => setField(field.key, e.target.value)}
          />
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )

    // ── textarea / note ───────────────────────────────────────────────────────
    case "textarea":
    case "note": {
      const words = countWords(value)
      const overMax = field.maxWords && words > field.maxWords
      // Under-minimum only bites once there is something to count: an empty
      // required field is the required-field error, not a word-count one.
      const underMin = field.minWords && words > 0 && words < field.minWords
      return (
        <Wrap>
          <FieldLabel htmlFor={id} optional={field.type === "note" ? false : optional}>
            {field.label}
            {field.maxWords && (
              <span className="text-[11px] font-semibold text-muted-foreground">
                {field.minWords ? `${field.minWords}–${field.maxWords} words` : `${field.maxWords} words max`}
              </span>
            )}
          </FieldLabel>
          <Help>{field.help}</Help>
          <Textarea
            id={id}
            rows={field.rows ?? 4}
            value={value ?? ""}
            placeholder={field.placeholder}
            aria-invalid={!!error || overMax || underMin}
            onChange={(e) => setField(field.key, e.target.value)}
          />
          {field.maxWords && (
            <p
              className={cn(
                "text-right text-[11px] font-semibold",
                overMax || underMin ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {words} / {field.maxWords} words
            </p>
          )}
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── number ────────────────────────────────────────────────────────────────
    case "number":
      return (
        <Wrap>
          <FieldLabel htmlFor={id} optional={optional}>
            {field.label}
          </FieldLabel>
          <Help>{field.help}</Help>
          <Input
            id={id}
            type="number"
            value={value ?? ""}
            min={field.min}
            max={field.max}
            step={field.step}
            placeholder={field.placeholder}
            aria-invalid={!!error}
            onChange={(e) => setField(field.key, e.target.value === "" ? "" : Number(e.target.value))}
          />
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )

    // ── count (spinner) ───────────────────────────────────────────────────────
    case "count": {
      const step = field.step ?? 1
      const min = field.min ?? 0
      const max = field.max
      const n = typeof value === "number" ? value : min
      const set = (next) => {
        let v = next
        if (typeof min === "number") v = Math.max(min, v)
        if (typeof max === "number") v = Math.min(max, v)
        setField(field.key, v)
      }
      return (
        <Wrap>
          <FieldLabel htmlFor={id} optional={optional}>
            {field.label}
          </FieldLabel>
          <Help>{field.help}</Help>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Decrease"
              onClick={() => set(n - step)}
              className="flex size-9 items-center justify-center rounded-md border border-line-2 bg-card text-foreground transition-colors hover:bg-secondary"
            >
              <Minus className="size-4" strokeWidth={2} />
            </button>
            <Input
              id={id}
              type="number"
              className="w-20 text-center"
              value={value ?? ""}
              min={min}
              max={max}
              step={step}
              aria-invalid={!!error}
              onChange={(e) => set(e.target.value === "" ? min : Number(e.target.value))}
            />
            <button
              type="button"
              aria-label="Increase"
              onClick={() => set(n + step)}
              className="flex size-9 items-center justify-center rounded-md border border-line-2 bg-card text-foreground transition-colors hover:bg-secondary"
            >
              <Plus className="size-4" strokeWidth={2} />
            </button>
            {field.unit && (
              <span className="text-[12.5px] font-semibold text-muted-foreground">{field.unit}</span>
            )}
          </div>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── wordcount ─────────────────────────────────────────────────────────────
    case "wordcount": {
      if (field.fixedLabel) {
        return (
          <Wrap>
            <FieldLabel>{field.label}</FieldLabel>
            <Help>{field.help}</Help>
            <div className="flex h-9 items-center rounded-md border border-line-2 bg-secondary px-3 text-[13.5px] font-semibold text-muted-foreground">
              {field.fixedLabel}
            </div>
          </Wrap>
        )
      }
      // The design's word-count control (service.html `wordsControl`): a large
      // value readout, a range slider, and preset chips. The slider ceiling is
      // 250,000 for the high-floor manuscript fields (field.min >= 10,000) and
      // 200,000 otherwise; presets are the design's set filtered to the floor.
      const min = field.min ?? 0
      const step = field.step ?? 100
      const sliderMax = field.min >= 10000 ? 250000 : 200000
      const val = Math.max(min, Number(value) || min)
      const presets =
        field.min >= 10000
          ? [10000, 25000, 30000, 50000, 90000]
          : [2500, 10000, 25000, 30000, 50000, 90000].filter((w) => w >= min)
      return (
        <Wrap>
          <FieldLabel htmlFor={id} optional={optional}>
            {field.label}
            <span className="text-[11px] font-semibold text-muted-foreground">
              Min {min.toLocaleString()} &middot; steps of {step.toLocaleString()}
            </span>
          </FieldLabel>
          <Help>{field.help}</Help>
          <div className="flex flex-col gap-3.5 rounded-xl border border-line-2 bg-card px-4 py-3.5">
            {/* The big readout is an editable number input so the exact count can
                still be typed (and keeps the stable #f-<key> id the flow relies
                on); the slider + presets are the design's primary control. */}
            <div className="flex items-baseline gap-1.5">
              <Input
                id={id}
                type="number"
                min={min}
                max={field.max ?? SERVER_WORD_MAX}
                step={step}
                value={value ?? ""}
                aria-invalid={!!error}
                onChange={(e) => setField(field.key, e.target.value === "" ? "" : Number(e.target.value))}
                className="h-auto w-[7ch] border-0 bg-transparent p-0 font-display text-[26px] font-bold leading-none tracking-tight text-foreground shadow-none focus-visible:ring-0"
              />
              <span className="text-[12px] font-semibold text-muted-foreground">words</span>
            </div>
            <Slider
              min={min}
              max={sliderMax}
              step={step}
              value={[Math.min(val, sliderMax)]}
              aria-label={field.label}
              onValueChange={(arr) => setField(field.key, Math.max(min, arr[0]))}
            />
            <div className="flex flex-wrap gap-2">
              {presets.map((w) => {
                const on = val === w
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setField(field.key, w)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors",
                      on
                        ? "border-primary bg-[var(--red-soft)] text-primary"
                        : "border-line-2 text-muted-foreground hover:border-ink-3"
                    )}
                  >
                    {w / 1000}k
                  </button>
                )
              })}
            </div>
          </div>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── radio (vertical option cards) ─────────────────────────────────────────
    case "radio": {
      const opts = resolveOptions(field, values, ctx)
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          <RadioGroup value={value == null ? "" : String(value)} onValueChange={(v) =>
            setField(field.key, opts.find((o) => String(o.value) === v)?.value ?? v)
          } className="gap-2.5">
            {opts.map((o, index) => {
              const on = value != null && String(value) === String(o.value)
              const optionLabelId = `${id}-option-${index}-label`
              return (
                <div
                  key={o.value}
                  onClick={() => setField(field.key, o.value)}
                  className={cn(CARD_BASE, "min-w-0 cursor-pointer", on ? CARD_ON : CARD_OFF)}
                >
                  <RadioGroupItem
                    value={String(o.value)}
                    id={`${id}-${o.value}`}
                    aria-labelledby={optionLabelId}
                    className="pointer-events-none mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    {/* A grid item never narrower than its content would push the
                        cards past a phone's width; a price that does not fit beside
                        the name and tag wraps under them instead. */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span id={optionLabelId} className="font-display text-[14px] font-bold text-foreground">{o.label}</span>
                      {o.tag && (
                        <span className="rounded-md bg-done/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-done">
                          {o.tag}
                        </span>
                      )}
                      {o.price && (
                        <span className="ml-auto whitespace-nowrap font-display text-[12.5px] font-bold text-muted-foreground">
                          {o.price}
                        </span>
                      )}
                    </div>
                    {o.sub && (
                      <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{o.sub}</p>
                    )}
                    {Array.isArray(o.includes) && o.includes.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                          Includes
                        </div>
                        <ul className="mt-1 flex flex-col gap-1">
                          {o.includes.map((item, i) => (
                            <li
                              key={i}
                              className="relative pl-4 text-[12px] leading-snug text-muted-foreground before:absolute before:left-1 before:top-[7px] before:size-1 before:rounded-full before:bg-ink-3"
                            >
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </RadioGroup>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── notice (static info banner; no key, no value) ─────────────────────────
    case "notice":
      return (
        <div className="flex items-start gap-2.5 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-relaxed text-muted-foreground">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          <div>{field.text}</div>
        </div>
      )

    // ── select ────────────────────────────────────────────────────────────────
    case "select": {
      const opts = resolveOptions(field, values, ctx)
      const choices = opts.flatMap((o) => Array.isArray(o?.options) ? o.options : [o])
      const selected = value ?? field.default
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          <Select
            value={selected == null || selected === "" ? undefined : String(selected)}
            onValueChange={(v) => setField(field.key,
              choices.find((o) => String(o.value) === v)?.value ?? v)}
          >
            <SelectTrigger className="w-full" aria-invalid={!!error}>
              <SelectValue placeholder={field.blankLabel ?? "Select an option"} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((o) =>
                // A grouped entry ({ group, options }) renders a labelled
                // section; a plain one renders a single item. Illustration and
                // cover use groups to keep fiction genres apart from
                // non-fiction niches in one "Niche / genre" list (AU-25).
                o && Array.isArray(o.options) ? (
                  <SelectGroup key={o.group}>
                    <SelectLabel>{o.group}</SelectLabel>
                    {o.options.map((item) => (
                      <SelectItem key={item.value} value={String(item.value)}>
                        {item.label ?? item.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label ?? o.value}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── checkgroup ────────────────────────────────────────────────────────────
    case "checkgroup": {
      const opts = resolveOptions(field, values, ctx)
      const arr = Array.isArray(value) ? value : []
      const atMax = field.max && arr.length >= field.max
      const toggle = (v) => {
        if (arr.includes(v)) setField(field.key, arr.filter((x) => x !== v))
        else if (!atMax) setField(field.key, [...arr, v])
      }
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          {(field.min || field.max) && (
            <p className="-mt-0.5 text-[11.5px] text-muted-foreground">
              {field.min ? `Select at least ${field.min}` : ""}
              {field.min && field.max ? " · " : ""}
              {field.max ? `up to ${field.max}` : ""}
            </p>
          )}
          <div className="flex flex-col gap-2.5">
            {opts.map((o) => {
              const on = arr.includes(o.value)
              const disabled = !on && atMax
              return (
                <div
                  key={o.value}
                  onClick={() => !disabled && toggle(o.value)}
                  className={cn(
                    CARD_BASE,
                    on ? CARD_ON : CARD_OFF,
                    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  )}
                >
                  <Checkbox checked={on} disabled={disabled} className="pointer-events-none mt-0.5" />
                  <span className="text-[13px] font-semibold text-foreground">{o.label ?? o.value}</span>
                </div>
              )
            })}
          </div>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── file (capture the real File; the autosave uploads it to storage) ──────
    case "file": {
      const chosen = value && value.name ? value : null
      return (
        <Wrap>
          <FieldLabel optional={optional} required={required}>
            {field.label}
          </FieldLabel>
          <Help>{field.help}</Help>
          {chosen ? (
            <div className="flex items-center gap-3 rounded-[10px] border border-border bg-card px-3.5 py-2.5">
              <span className="flex size-8 items-center justify-center rounded-lg bg-secondary">
                <FileText className="size-4 text-muted-foreground" strokeWidth={1.9} />
              </span>
              <span className="truncate text-[12.5px] font-semibold text-foreground">{chosen.name}</span>
              <button
                type="button"
                aria-label="Remove file"
                onClick={() => setField(field.key, null)}
                className="ml-auto flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <X className="size-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : (
            <label className="flex cursor-pointer flex-col items-center gap-1.5 rounded-[11px] border border-dashed border-line-2 px-4 py-5 text-center transition-colors hover:border-ink-3 hover:bg-accent">
              <Upload className="size-5 text-muted-foreground" strokeWidth={1.8} />
              <span className="text-[12.5px] font-semibold text-muted-foreground">
                {field.placeholder ?? "Upload a file"}
              </span>
              {field.accept && <span className="text-[11px] text-muted-foreground/80">Supported: {field.accept}</span>}
              <input
                type="file"
                accept={field.accept}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0]
                  if (f) setField(field.key, f)
                }}
              />
            </label>
          )}
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── gallery (sample image tiles, multi-select) ────────────────────────────
    case "gallery": {
      const arr = Array.isArray(value) ? value : []
      const count = field.count ?? 12
      const atMax = field.max && arr.length >= field.max
      // Design gallery idiom (service.html `galleryField`): a header naming the
      // sample kind + the pick range, and a live "N/max selected" counter that
      // turns red below the minimum. `noun` drives both the header and the
      // per-tile label; the persisted tile value stays the backend filename.
      const galleryNoun = field.noun || "Sample"
      const belowMin = field.min ? arr.length < field.min : false
      // Tile id = the value that PERSISTS. Design services supply `valueFor` so the
      // stored pick is the backend filename (cover_sample_0.png), not a bare index
      // that the server can't render (Plumbline bug 1). `imageFor` resolves the real
      // sample image so tiles are sighted (Plumbline bug 3).
      const valueFor = typeof field.valueFor === "function" ? field.valueFor : (i) => String(i + 1)
      const toggle = (tileId) => {
        if (arr.includes(tileId)) setField(field.key, arr.filter((x) => x !== tileId))
        else if (!atMax) setField(field.key, [...arr, tileId])
      }
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] font-semibold text-muted-foreground">
              {galleryNoun} samples
              {field.min && field.max ? `, pick ${field.min}-${field.max}` : ""}
            </span>
            {field.max && (
              <span
                data-gallery-counter={field.key}
                className={cn(
                  "text-[12px] font-semibold",
                  belowMin ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {arr.length}/{field.max} selected
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
            {Array.from({ length: count }, (_, i) => {
              const tileId = valueFor(i)
              const on = arr.includes(tileId)
              return (
                <SampleTile
                  key={tileId}
                  label={`${galleryNoun} ${String(i + 1).padStart(2, "0")}`}
                  imageSrc={typeof field.imageFor === "function" ? field.imageFor(tileId) : null}
                  alt={tileId}
                  shape={field.tileShape}
                  on={on}
                  disabled={!on && atMax}
                  onToggle={() => toggle(tileId)}
                  placeholder={
                    <svg className="size-full" viewBox="0 0 40 40" preserveAspectRatio="none" aria-hidden="true">
                      <defs>
                        <pattern id={`stripe-${id}-${i}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                          <rect width="6" height="6" fill="var(--secondary)" />
                          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--line-2)" strokeWidth="3" />
                        </pattern>
                      </defs>
                      <rect width="40" height="40" fill={`url(#stripe-${id}-${i})`} />
                    </svg>
                  }
                />
              )
            })}
          </div>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── palette (swatch checkgroup — stores KEYS) ─────────────────────────────
    case "palette": {
      const arr = Array.isArray(value) ? value : []
      const palettes = (ctx.options && ctx.options.COLOR_PALETTES) || []
      const toggle = (key) => {
        if (arr.includes(key)) setField(field.key, arr.filter((x) => x !== key))
        else setField(field.key, [...arr, key])
      }
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          {field.min && (
            <p className="-mt-0.5 text-[11.5px] text-muted-foreground">Pick at least {field.min}</p>
          )}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {palettes.map((p) => {
              const on = arr.includes(p.key)
              const cols = PALETTE_SWATCHES[p.label] || ["#ccc", "#ddd", "#eee"]
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggle(p.key)}
                  aria-pressed={on}
                  className={cn(
                    "flex flex-col gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors",
                    on ? "border-primary bg-[var(--red-soft)]" : "border-line-2 hover:border-ink-3"
                  )}
                >
                  <span className="flex h-5 overflow-hidden rounded-md border border-line-2">
                    {cols.map((c, i) => (
                      <i key={i} className="flex-1" style={{ background: c }} />
                    ))}
                  </span>
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-[12px] font-semibold text-foreground">{p.label}</span>
                    <span
                      className={cn(
                        "size-3 shrink-0 rounded-full border",
                        on ? "border-primary bg-primary" : "border-line-2"
                      )}
                    />
                  </span>
                </button>
              )
            })}
          </div>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── sliders (N axes, 1..5, default 3) ─────────────────────────────────────
    case "sliders": {
      const axes = field.axes ?? []
      const moved = axes.filter((a) => {
        const v = values[a.key]
        return typeof v === "number" && v !== 3
      }).length
      const needMore = field.minMoved && moved < field.minMoved
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          <div className="flex flex-col gap-5">
            {axes.map((a) => {
              const v = typeof values[a.key] === "number" ? values[a.key] : 3
              return (
                <div key={a.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="font-semibold text-muted-foreground">{a.leftLabel}</span>
                    {a.label && <span className="font-semibold text-foreground">{a.label}</span>}
                    <span className="font-semibold text-muted-foreground">{a.rightLabel}</span>
                  </div>
                  <Slider
                    min={1}
                    max={5}
                    step={1}
                    value={[v]}
                    onValueChange={(arr) => setField(a.key, arr[0])}
                  />
                </div>
              )
            })}
          </div>
          {needMore && !error && (
            <p className="text-[11.5px] font-semibold text-muted-foreground">
              Move at least {field.minMoved} sliders to guide your designer.
            </p>
          )}
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── toggle (design checkbox-card idiom + optional revealed detail) ────────
    case "toggle": {
      const on = !!value
      const detailsRequired = on && !!field.detailsRequired
      return (
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setField(field.key, !on)}
            aria-pressed={on}
            className={cn(CARD_BASE, "cursor-pointer items-start", on ? CARD_ON : CARD_OFF)}
          >
            <span
              className={cn(
                "mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border",
                on ? "border-primary bg-primary text-primary-foreground" : "border-line-2"
              )}
            >
              {on && <Check className="size-3" strokeWidth={3} />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-foreground">{field.label}</div>
              {field.help && (
                <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{field.help}</p>
              )}
            </div>
          </button>
          {on && field.detailsKey && (
            <div className="flex flex-col gap-2 rounded-xl border border-border bg-accent px-3.5 py-3">
              {field.detailsLabel && (
                <FieldLabel optional={!detailsRequired}>{field.detailsLabel}</FieldLabel>
              )}
              {field.detailsType === "text" ? (
                <Input
                  value={values[field.detailsKey] ?? ""}
                  placeholder={field.detailsPlaceholder}
                  aria-invalid={!!error}
                  onChange={(e) => setField(field.detailsKey, e.target.value)}
                />
              ) : (
                <Textarea
                  rows={field.detailsRows ?? 3}
                  value={values[field.detailsKey] ?? ""}
                  placeholder={field.detailsPlaceholder}
                  aria-invalid={!!error}
                  onChange={(e) => setField(field.detailsKey, e.target.value)}
                />
              )}
            </div>
          )}
          <ErrorText>{error}</ErrorText>
        </div>
      )
    }

    // ── consent (AI-consent radio; §3.1) ──────────────────────────────────────
    case "consent": {
      const carrier = field.key || "ai_consent"
      const current = values[carrier]
      // Design services (mode "2": cover design + illustration) consent to the
      // design AI scope (_design_ai_consent.html.erb), not the writing scope
      // (Plumbline bug 2). mode "3" (writing) keeps AI_CONSENT_ALL_BULLETS.
      const bullets =
        (ctx.options &&
          (field.mode === "2"
            ? ctx.options.DESIGN_AI_CONSENT_ALL_BULLETS
            : ctx.options.AI_CONSENT_ALL_BULLETS)) ||
        []
      // Design labels (options.AI_CONSENT_LABELS, sourced from design
      // service-config.js AI_CONSENT) replace the build's invented short labels
      // (Plumbline Phase 2 WI-A6). Fall back to the old strings if the module
      // key is absent so an un-threaded ctx still renders.
      const consentLabels = (ctx.options && ctx.options.AI_CONSENT_LABELS) || {}
      const none = { value: "none", label: consentLabels.none || "No AI use" }
      const research = {
        value: "research",
        label: consentLabels.research || "AI for research and brainstorming only",
      }
      const all = { value: "all", label: consentLabels.all || "Allow broader AI use", bullets }
      const opts = field.mode === "2" ? [none, all] : [none, research, all]
      return (
        <Wrap>
          {field.label && <FieldLabel optional={optional}>{field.label}</FieldLabel>}
          <Help>{field.help}</Help>
          <RadioGroup
            value={current ?? ""}
            onValueChange={(v) => setField(carrier, v)}
            className="gap-2.5"
          >
            {opts.map((o, index) => {
              const on = current === o.value
              const optionLabelId = `${id}-option-${index}-label`
              return (
                <div
                  key={o.value}
                  onClick={() => setField(carrier, o.value)}
                  className={cn(CARD_BASE, "cursor-pointer", on ? CARD_ON : CARD_OFF)}
                >
                  <RadioGroupItem
                    value={o.value}
                    aria-labelledby={optionLabelId}
                    className="pointer-events-none mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <span id={optionLabelId} className="font-display text-[14px] font-bold text-foreground">{o.label}</span>
                    {o.bullets && o.bullets.length > 0 && (
                      <ul className="mt-2 flex flex-col gap-1">
                        {o.bullets.map((b, i) => (
                          <li
                            key={i}
                            className="relative pl-4 text-[12px] leading-snug text-muted-foreground before:absolute before:left-1 before:top-[7px] before:size-1 before:rounded-full before:bg-ink-3"
                          >
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )
            })}
          </RadioGroup>
          <p className="flex items-start gap-2 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12px] leading-snug text-muted-foreground">
            <span>
              Allowing freelancers to leverage AI can speed up delivery while keeping quality high. See our{" "}
              <a
                href="https://theurbanwriters.com/pages/acceptable-uses-of-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground underline underline-offset-2"
              >
                Acceptable Uses of AI
              </a>{" "}
              disclaimer.
            </span>
          </p>
          <ErrorText>{error}</ErrorText>
        </Wrap>
      )
    }

    // ── triggerWarning (toggle + 8-cat checkgroup + Other; §3.2) ──────────────
    case "triggerWarning": {
      const on = !!values.trigger_warning
      const cats = (ctx.options && ctx.options.TRIGGER_CATEGORIES) || []
      const selected = Array.isArray(values.trigger_warning_warnings) ? values.trigger_warning_warnings : []
      const toggleCat = (c) => {
        if (selected.includes(c)) setField("trigger_warning_warnings", selected.filter((x) => x !== c))
        else setField("trigger_warning_warnings", [...selected, c])
      }
      return (
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">
                {field.label || "This book contains sensitive or triggering content"}
              </div>
              <Help>{field.help}</Help>
            </div>
            <Switch
              checked={on}
              onCheckedChange={(v) => setField("trigger_warning", v)}
            />
          </div>
          {on && (
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-accent px-3.5 py-3">
              <FieldLabel required>Which apply?</FieldLabel>
              <div className="grid gap-2 sm:grid-cols-2">
                {cats.map((c) => {
                  const checked = selected.includes(c)
                  return (
                    <div
                      key={c}
                      onClick={() => toggleCat(c)}
                      className={cn(
                        "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors",
                        checked ? CARD_ON : "border-line-2 bg-card hover:border-ink-3"
                      )}
                    >
                      <Checkbox checked={checked} className="pointer-events-none mt-0.5" />
                      <span className="text-[12.5px] font-semibold text-foreground">{c}</span>
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel optional>Other</FieldLabel>
                <Input
                  value={values.trigger_warning_other ?? ""}
                  placeholder="Anything else readers should be warned about"
                  onChange={(e) => setField("trigger_warning_other", e.target.value)}
                />
              </div>
            </div>
          )}
          <ErrorText>{error}</ErrorText>
        </div>
      )
    }

    default:
      return (
        <Wrap>
          <ErrorText>{`Unknown field type: ${field.type}`}</ErrorText>
        </Wrap>
      )
  }
}
